import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  UserPlus,
  LogIn,
  KeyRound,
  ShieldAlert,
  Zap,
  ShieldCheck,
  Plug,
  CheckCircle,
  MessageSquare,
  Smartphone,
  Megaphone,
  Megaphone as RcsIcon,
  PhoneCall,
  CreditCard,
  Gamepad2,
  Antenna,
  ShoppingCart,
  Building,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const useCases = [
  {
    title: "Account Sign-Up Verification",
    description: "Confirm new users with a one-time code before granting access.",
    Icon: UserPlus,
    color: "text-amber",
    tags: ["SMS"],
  },
  {
    title: "Login Verification",
    description: "Add a verification step to flag suspicious or new-device logins.",
    Icon: LogIn,
    color: "text-sky",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Password Reset Verification",
    description: "Ensure only the account owner can reset credentials.",
    Icon: KeyRound,
    color: "text-violet",
    tags: ["SMS"],
  },
  {
    title: "High-Risk Transaction Verification",
    description: "Require verification before high-value or sensitive actions complete.",
    Icon: ShieldAlert,
    color: "text-rose",
    tags: ["SMS", "Voice"],
  },
];

const benefits = [
  {
    title: "Reliable Delivery",
    description: "Verification codes reach users quickly, wherever they are.",
    Icon: Zap,
    color: "text-amber",
  },
  {
    title: "Fraud Prevention",
    description: "Stop fake accounts and account takeover attempts before they start.",
    Icon: ShieldCheck,
    color: "text-sky",
  },
  {
    title: "Seamless Integration",
    description: "Add verification to existing sign-up and login flows with minimal engineering effort.",
    Icon: Plug,
    color: "text-lime",
  },
];

const industries = [
  { title: "Fintech & Payments", to: "/industries/fintech-payments", Icon: CreditCard, gradient: "icon-tile-teal" },
  { title: "Digital Marketing Agencies", to: "/industries/digital-marketing-agencies", Icon: Megaphone, gradient: "icon-tile-indigo" },
  { title: "iGaming", to: "/industries/igaming", Icon: Gamepad2, gradient: "icon-tile-blue" },
  { title: "Telecom & CPaaS", to: "/industries/telecom-cpaas", Icon: Antenna, gradient: "icon-tile-orange" },
  { title: "Retail & Ecommerce", to: "/industries/retail-ecommerce", Icon: ShoppingCart, gradient: "icon-tile-pink" },
  { title: "Real Estate", to: "/industries/real-estate", Icon: Building, gradient: "icon-tile-green" },
];

const supportedChannels = [
  { title: "SMS", description: "Deliver verification codes reliably to any mobile number.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Deliver richer, interactive verification experiences.", Icon: RcsIcon, gradient: "icon-tile-indigo" },
  { title: "Voice", description: "Read codes aloud for users who can't receive messages.", Icon: PhoneCall, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "What is user verification and why does my business need it?", answer: "User verification confirms that the person behind a sign-up, login, or transaction is who they claim to be. It stops fraudulent accounts, protects existing users from takeover attempts, and secures sensitive actions without adding friction for genuine users." },
  { question: "Which channels can be used to verify users?", answer: "Yootelco supports verification over SMS, RCS, and Voice, so you can reach users on the channels they already trust and fall back automatically when needed." },
  { question: "How fast are verification codes delivered?", answer: "Codes are delivered within seconds through direct carrier connections and smart routing that selects the fastest available path for each destination." },
  { question: "Can user verification be added to an existing sign-up flow?", answer: "Yes. Verification integrates into your current sign-up, login, and password reset flows with a simple API call, so your existing user experience stays intact." },
  { question: "Does Yootelco support verification for high-risk transactions?", answer: "Yes. You can require verification before high-value payments, account changes, or other sensitive actions complete, using SMS or Voice delivery." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const channelIcons = [
  { label: "SMS", Icon: MessageSquare },
  { label: "RCS", Icon: RcsIcon },
];

const SolutionUserVerification = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="User Verification & Identity Checks | Yootelco"
        description="Confirm real users at sign-up and login with carrier-grade verification built for banks, fintechs, and enterprises. Talk to our team."
        path="/solutions/user-verification"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              User Verification
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Verify Every User, Instantly
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Confirm real users at sign-up, login, and high-risk moments with fast, reliable verification across SMS and RCS.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {channelIcons.map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <channel.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {channel.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Is User Verification */}
      <section className="section-shell pt-[20px] md:pt-[40px]">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">What Is User Verification?</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            User verification confirms that the person behind an action is who they claim to be. Businesses use it to stop fraudulent sign-ups, secure logins, and protect high-risk transactions, without adding friction for genuine users. Yootelco delivers verification codes over the channels your users already trust, with fast delivery and dependable uptime.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Use Cases</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {useCases.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} className="mx-auto block w-fit" />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Benefits */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Benefits</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Industries */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Industries We Serve</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {industries.map((item) => {
            const Icon = item.Icon;
            return (
              <Link
                key={item.title}
                to={item.to}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${item.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Multi-channel delivery across SMS, RCS, and Voice",
              "Built for time-critical verification traffic",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Ready to Verify Every User?"
        description="Talk to our team about adding user verification to your platform."
        useCase="User Verification"
      />

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Get Started with User Verification</h2>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default SolutionUserVerification;
